import {type GameAssets, GameConfig, type Sprite} from './game-types';
import type {GameEngine} from './game-engine';

export const characterUrls = GameConfig.CHARACTER_FILES.map(
  (file) => `${import.meta.env.VITE_STATIC_ASSETS_URL}/${file}`,
);
export const coneUrl = `${import.meta.env.VITE_STATIC_ASSETS_URL}/cone.png`;
export const lampUrl = `${import.meta.env.VITE_STATIC_ASSETS_URL}/lamp.png`;
export const plungerUrl = `${import.meta.env.VITE_STATIC_ASSETS_URL}/plunger.gif`;
export const cartUrl = `${import.meta.env.VITE_STATIC_ASSETS_URL}/cart.gif`;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export async function loadAssets(): Promise<GameAssets> {
  const [coneImg, lampImg, plungerImg, cartImg] = await Promise.all([
    loadImage(coneUrl),
    loadImage(lampUrl),
    loadImage(plungerUrl),
    loadImage(cartUrl),
  ]);

  return {
    cone: { image: coneImg, width: GameConfig.CONE_WIDTH, height: GameConfig.CONE_HEIGHT },
    lamp: { image: lampImg, width: GameConfig.LAMP_WIDTH, height: GameConfig.LAMP_HEIGHT },
    plunger: {
      image: plungerImg,
      width: GameConfig.PLUNGER_WIDTH,
      height: GameConfig.PLUNGER_HEIGHT,
      // Source crop: the plunger art only occupies a vertical band in the middle
      // of its 512x512 canvas. Without this crop the sprite is stretched to the
      // full canvas and looks like a flat line at 30px tall.
      srcX: 0,
      srcY: 176,
      srcW: 512,
      srcH: 160,
    },
    cart: { image: cartImg, width: GameConfig.CART_WIDTH, height: GameConfig.CART_HEIGHT },
  };
}

const COLORS = {
  background: '#e8e8e8',
  wallPanel: '#d4d4d4',
  wallLine: '#c8c8c8',
  floorBase: '#b8b8b8',
  floorStripe: '#a8a8a8',
  floorEdge: '#9a9a9a',
  textPrimary: '#404040',
  textSecondary: '#808080',
  overlay: 'rgba(232, 232, 232, 0.75)',
};

export class GameRenderer {
  private ctx: CanvasRenderingContext2D;
  private assets: GameAssets;
  private pulseFrame = 0;

  constructor(ctx: CanvasRenderingContext2D, assets: GameAssets) {
    this.ctx = ctx;
    this.assets = assets;
    this.ctx.imageSmoothingEnabled = false;
  }

  render(engine: GameEngine) {
    const { ctx } = this;
    const W = GameConfig.CANVAS_WIDTH;
    const H = GameConfig.CANVAS_HEIGHT;

    // Clear
    ctx.clearRect(0, 0, W, H);

    // Background
    ctx.fillStyle = COLORS.background;
    ctx.fillRect(0, 0, W, H);

    // Wall panels (parallax scroll — slower than floor)
    this.drawWallPanels(engine.lastFrameMovement);

    // Floor
    this.drawFloor(engine.floorOffset);

    // Obstacles
    for (const obstacle of engine.obstacles) {
      const sprite = {
        floor: this.assets.cone,
        upper: this.assets.lamp,
        plunger: this.assets.plunger,
        cart: this.assets.cart,
      }[obstacle.type];
      this.drawSprite(sprite, obstacle.x, obstacle.y, obstacle.width, obstacle.height);
    }

    // Player is rendered as a DOM <img> overlay for GIF animation

    // HUD
    this.drawScore(engine.score, engine.highScore);
  }

  renderIdleScreen(engine: GameEngine, dt: number) {
    this.render(engine);
    this.pulseFrame += dt;

    const { ctx } = this;
    const W = GameConfig.CANVAS_WIDTH;

    // Pulsing text
    const alpha = 0.5 + 0.5 * Math.sin(this.pulseFrame * 0.05);
    ctx.globalAlpha = alpha;
    ctx.fillStyle = COLORS.textPrimary;
    ctx.font = 'bold 18px monospace';
    ctx.textAlign = 'center';
    ctx.fillText('Press Space or Click to Start', W / 2, 130);
    ctx.globalAlpha = 1;
    ctx.textAlign = 'start';
  }

  renderGameOver(engine: GameEngine) {
    this.render(engine);

    const { ctx } = this;
    const W = GameConfig.CANVAS_WIDTH;

    // Overlay
    ctx.fillStyle = COLORS.overlay;
    ctx.fillRect(0, 0, W, GameConfig.CANVAS_HEIGHT);

    // Game over text
    ctx.fillStyle = COLORS.textPrimary;
    ctx.font = 'bold 24px monospace';
    ctx.textAlign = 'center';
    ctx.fillText('GAME OVER', W / 2, 120);

    ctx.font = '16px monospace';
    ctx.fillStyle = COLORS.textSecondary;
    ctx.fillText('Press Space or Click to Restart', W / 2, 155);
    ctx.textAlign = 'start';
  }

  private wallOffset = 0;

  private drawWallPanels(floorOffset: number) {
    const { ctx } = this;
    const W = GameConfig.CANVAS_WIDTH;
    const panelTop = 10;
    const panelBottom = GameConfig.GROUND_Y - 20;
    const panelWidth = W / 3;

    // Track wall scroll — parallax at half floor speed
    this.wallOffset = (this.wallOffset + floorOffset * 0.30);

    // Wall panel background
    ctx.fillStyle = COLORS.wallPanel;
    ctx.fillRect(0, panelTop, W, panelBottom - panelTop);

    // Horizontal line across middle
    ctx.strokeStyle = COLORS.wallLine;
    ctx.lineWidth = 1;
    const midY = (panelTop + panelBottom) / 2;
    ctx.beginPath();
    ctx.moveTo(0, midY);
    ctx.lineTo(W, midY);
    ctx.stroke();

    // Scrolling vertical dividers
    const startX = -(this.wallOffset % panelWidth);
    for (let x = startX; x < W; x += panelWidth) {
      ctx.beginPath();
      ctx.moveTo(x, panelTop);
      ctx.lineTo(x, panelBottom);
      ctx.stroke();
    }
  }

  private drawFloor(offset: number) {
    const { ctx } = this;
    const W = GameConfig.CANVAS_WIDTH;
    const groundY = GameConfig.GROUND_Y;
    const floorH = GameConfig.FLOOR_HEIGHT;

    // Floor base
    ctx.fillStyle = COLORS.floorBase;
    ctx.fillRect(0, groundY, W, floorH);

    // Top edge line
    ctx.fillStyle = COLORS.floorEdge;
    ctx.fillRect(0, groundY, W, 2);

    // Diagonal stripes
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, groundY + 2, W, floorH - 4);
    ctx.clip();

    ctx.strokeStyle = COLORS.floorStripe;
    ctx.lineWidth = 2;
    const stripeSpacing = 20;
    const startX = -floorH - stripeSpacing + (offset % stripeSpacing);

    for (let x = startX; x < W + floorH; x += stripeSpacing) {
      ctx.beginPath();
      ctx.moveTo(x, groundY + 2);
      ctx.lineTo(x + floorH, groundY + floorH);
      ctx.stroke();
    }

    ctx.restore();

    // Bottom edge
    ctx.fillStyle = COLORS.floorEdge;
    ctx.fillRect(0, groundY + floorH - 2, W, 2);
  }

  private drawSprite(sprite: Sprite, x: number, y: number, w: number, h: number) {
    if (sprite.srcW !== undefined && sprite.srcH !== undefined) {
      this.ctx.drawImage(
        sprite.image,
        sprite.srcX ?? 0,
        sprite.srcY ?? 0,
        sprite.srcW,
        sprite.srcH,
        x,
        y,
        w,
        h,
      );
    } else {
      this.ctx.drawImage(sprite.image, x, y, w, h);
    }
  }

  private drawScore(score: number, highScore: number) {
    const { ctx } = this;
    const W = GameConfig.CANVAS_WIDTH;

    ctx.fillStyle = COLORS.textPrimary;
    ctx.font = 'bold 14px monospace';
    ctx.textAlign = 'right';

    const scoreStr = String(Math.floor(score)).padStart(5, '0');
    const hiStr = String(Math.floor(highScore)).padStart(5, '0');

    ctx.fillText(`HI ${hiStr}   ${scoreStr}`, W - 15, 25);
    ctx.textAlign = 'start';
  }
}
